<template></template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { RegistrationApiResponse } from '@/src/components/addons/registration/types';
import { events } from '@/src/services/events';
import { registerRepeatable, updateRepeatable } from '@/src/services/repeatable';
import type { AddonGameplayModel } from '@/src/components/addons/gameplay/Model';
import type { GamePollState } from '@/src/components/games/poll/Model';
import { GamePollModel } from '@/src/components/games/poll/Model';
import { ResultType } from '@/src/components/games/poll/Enums';
import { useCampaignStore } from '@/src/store/campaign';
import type { Distribution } from '@/src/components/games/poll/Utility';
import { getDistribution, getMyAnswers } from '@/src/components/games/poll/Utility';

interface VotesState {
  [key: string]: string | number;
}

export interface PollGameRegistrationResponse extends RegistrationApiResponse {
  votes?: VotesState;
}

interface PollGameRepeatableData {
  id: number | string;
  [key: string]: string | number;
}

export default defineComponent({
  name: 'PollGameInitializer',
  setup() {
    let updatedAnswerVotes: Distribution[];
    const campaignStore = useCampaignStore();

    // Answer outcome
    registerRepeatable('answer', async () => {
      const repeatableData: PollGameRepeatableData[] = [];
      const userAnswers = getMyAnswers();
      let pollGameState: GamePollState | undefined;

      campaignStore.model?.state.flowPages.forEach((pageModel) => {
        pageModel.getAddons<AddonGameplayModel>('gameplay').forEach((gameplayModel) => {
          const pollGameModel = gameplayModel.state.settings?.game;
          /**
           * Note here: We need to find a way to type this without using "as"
           * Spoke to Dannie on this, as we haven't found a feasible solution yet
           */
          if (pollGameModel instanceof GamePollModel) {
            pollGameState = pollGameModel?.state;
          }
        });
      });

      const distribution = getDistribution(
        (
          updatedAnswerVotes ??
          pollGameState?.answers?.map((answer) => {
            return {
              id: answer.id,
              votes: answer.votes
            };
          }) ??
          []
        ).map((item) => {
          return { ...item };
        })
      )
        .sort((a, b) => a.votes - b.votes)
        .reverse();

      if (pollGameState && pollGameState.answers) {
        pollGameState.answers.forEach((answer) => {
          const answerId = answer.id;
          const distributionItem = distribution.find((item) => item.id === answerId);

          if (distributionItem) {
            let votes = '0';
            let votesSorting = 0;

            if (pollGameState && pollGameState.general.resultIn === ResultType.NUMBERS) {
              votes = distributionItem.votes.toString() || '0';
              votesSorting = Number(votes);
            } else if (typeof distributionItem.votes_percentage !== 'undefined') {
              votes = distributionItem.votes_percentage.replace('.', ',');
              votesSorting = Number(distributionItem.votes_percentage);
            }

            // Filter out users own answers
            if (userAnswers.includes(String(answer.id))) {
              repeatableData.push({
                id: answerId,
                answer: answer.value,
                image: answer.value,
                headline: answer.headline ?? answer.headline2 + '',
                votes: votes ?? '0',
                votes_sorting: votesSorting,
                rank: distribution.findIndex((item) => item.id === answerId) + 1 || 0
              });
            }
          }
        });
      }
      return repeatableData;
    });

    // List all answers
    registerRepeatable('total_votes_summary', async () => {
      const repeatableData: PollGameRepeatableData[] = [];
      let pollGameState: GamePollState | undefined;

      campaignStore.model?.state.flowPages.forEach((pageModel) => {
        pageModel.getAddons<AddonGameplayModel>('gameplay').forEach((gameplayModel) => {
          const pollGameModel = gameplayModel.state.settings?.game;
          /**
           * Note here: We need to find a way to type this without using "as"
           * Spoke to Dannie on this, as we haven't found a feasible solution yet
           */
          if (pollGameModel instanceof GamePollModel) {
            pollGameState = pollGameModel?.state;
          }
        });
      });

      const distribution = getDistribution(
        (
          updatedAnswerVotes ??
          pollGameState?.answers?.map((answer) => {
            return {
              id: answer.id,
              votes: answer.votes
            };
          }) ??
          []
        ).map((item) => {
          return { ...item };
        })
      )
        .sort((a, b) => a.votes - b.votes)
        .reverse();

      if (pollGameState && pollGameState.answers) {
        pollGameState.answers.forEach((answer) => {
          const answerId = answer.id;
          const distributionItem = distribution.find((item) => item.id === answerId);

          if (distributionItem) {
            let votes = '0';
            if (pollGameState && pollGameState.general.resultIn === ResultType.NUMBERS) {
              votes = distributionItem.votes.toString() || '0';
            } else if (typeof distributionItem.votes_percentage !== 'undefined') {
              votes = distributionItem.votes_percentage.replace('.', ',');
            }

            repeatableData.push({
              id: answerId,
              answer: answer.value,
              image: answer.value,
              headline: answer.headline ?? answer.headline2 + '',
              votes: votes ?? '0',
              votes_sorting: Number(distributionItem.votes_percentage),
              rank: distribution.findIndex((item) => item.id === answerId) + 1 || 0
            });
          }
        });
      }

      return repeatableData;
    });

    events.on('formSubmitResponse', (event) => {
      const response: PollGameRegistrationResponse = event.detail.response;
      const answers: Distribution[] = [];

      if (response.votes) {
        for (const answer in response.votes) {
          answers.push({
            id: answer,
            // eslint-disable-next-line security/detect-object-injection
            votes: Number(response.votes[answer])
          });
        }
      }

      updatedAnswerVotes = answers;
      updateRepeatable('total_votes_summary');
      updateRepeatable('answer');
    });

    return {};
  }
});
</script>
